import React, {Component, ReactNode} from 'react'
import {Nullable} from '../../app/TypeHelpers'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {AssocTerm} from './model/AssocTerm'
import {formatTerm, makeTermNode, NoWrapSpan, WidthBuffer} from './shared/AssocComponents'
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import {AdminDefaultButton, AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {BackLink, DeleteLink, EditLink} from '../../styledComponents/adminPage/Links'
import AssocDirectorRepository from './AssocDirectorRepository'
import {
    LeftColumnFlexContainer,
    LeftRowFlexContainer,
    LeftTopRowFlexContainer,
} from '../../styledComponents/FlexComponents'
import {MESSAGE_TYPE, ROLE_FOR_ADMINISTRATOR, ROLE_FOR_ADMINISTRATOR_REAL} from '../../CodeMaster'
import moment, {Moment} from 'moment'
import {AssocDirectorPositionInfo} from './model/AssocDirectorPositionInfo'
import {Room} from './model/Room'
import {AssocDirectorHelper} from './utils/AssocDirectorHelper'
import {AdminPageHeadingArea} from '../shared/pageBaseComponents/AdminPageHeadingArea'
import {AssocDirector} from './model/AssocDirector'
import PPDatePicker from '../shared/forms/PPDatePicker'
import {Information} from '../../network/response/Information'
import NatAdminCommonAcceptedWarningComponent
    from '../../__NoAutomatedTests/components/common/admin/NatAdminCommonAcceptedWarningComponent'
import {judgeRoleCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import styled, {css} from 'styled-components'
import {Colors, FontSizeInPixel, FontWeights} from '../../styledComponents/Constants'
import {LightGrayTableHead} from '../../styledComponents/adminPage/Table'
import ReactDOM from 'react-dom'
import {Location} from 'react-router-dom'

export interface AssocDirectorPositionsPageLocationState {
    displayAssocName: Nullable<string>,
    selectedAssocId: number,
    selectedAssocTerm: object,
}

interface Props {
    condominiumCd: string
    assocDirectorRepo: AssocDirectorRepository
    eventEmitter: EventEmitterExAdmin
    currentDateTimeRepo: NatCurrentDateTimeRepo
    location: Location<AssocDirectorPositionsPageLocationState>
}

interface State {
    displayAssocName: Nullable<string>
    selectedAssocId: number
    assocTerm: AssocTerm
    currentDateTime: Nullable<Moment>
    assocDirectorList: AssocDirector[]
    chairmanList: PositionData[]
    viceChairmanList: PositionData[]
    dialogOpenRole: false | ROLE_FOR_ADMINISTRATOR_REAL
    editPositionData: Nullable<PositionData>
    editDialogMidtermAppointmentDate: Nullable<Date>
    editDialogMidtermTerminationDate: Nullable<Date>
    updateWarnings: Information[]
    acceptAllUpdateWarnings: boolean
}

interface PositionData extends AssocDirectorPositionInfo {
    assocDirectorId: number
    roomList: Room[]
    familyName: Nullable<string>
    firstName: Nullable<string>
}

class AdminAssocDirectorPositionListPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        const locationState = this.props.location?.state
        if (!locationState) {
            throw Error('前画面からの引き継ぎ情報がありません。画面遷移が不正です。')
        }

        this.state = {
            displayAssocName: locationState.displayAssocName,
            selectedAssocId: locationState.selectedAssocId,
            assocTerm: AssocTerm.from(locationState.selectedAssocTerm),
            currentDateTime: null,
            assocDirectorList: [],
            chairmanList: [],
            viceChairmanList: [],
            dialogOpenRole: false,
            editPositionData: null,
            editDialogMidtermAppointmentDate: null,
            editDialogMidtermTerminationDate: null,
            updateWarnings: [],
            acceptAllUpdateWarnings: false,
        }
    }

    async componentDidMount(): Promise<void> {
        await this.updatePositionList()
    }

    async updatePositionList(): Promise<void> {
        const {
            condominiumCd,
            assocDirectorRepo,
            currentDateTimeRepo,
            eventEmitter,
            asyncCallFunction,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const currentDateTime = await asyncCallFunction(
                currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (currentDateTime === null || currentDateTime.details === null) {
                return
            }

            const assocDirectorList = await asyncCallFunction(
                assocDirectorRepo.getAssocDirectors.bind(
                    assocDirectorRepo,
                    condominiumCd,
                    this.state.selectedAssocId,
                    this.state.assocTerm.assocTermId,
                ),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (assocDirectorList === null) {
                return
            }

            const positionList: PositionData[] = assocDirectorList
                .map(director => director.assocDirectorPositionInfoList
                    .map(position => ({
                        ...position,
                        assocDirectorId: director.assocDirectorId,
                        roomList: director.roomList,
                        familyName: director.familyName,
                        firstName: director.firstName,
                    })),
                )
                .flat()
            const chairmanList = positionList
                .filter(position => position.positionRoleId === ROLE_FOR_ADMINISTRATOR_REAL.ASSOC_CHAIRMAN.value)
                .sort(this.comparePositionData)
            const viceChairmanList = positionList
                .filter(position => position.positionRoleId === ROLE_FOR_ADMINISTRATOR_REAL.ASSOC_VICE_CHAIRMAN.value)
                .sort(this.comparePositionData)

            ReactDOM.flushSync(() => {
                this.setState({
                    currentDateTime: moment(currentDateTime.details!.currentDateTime),
                    assocDirectorList: assocDirectorList,
                    chairmanList: chairmanList,
                    viceChairmanList: viceChairmanList,
                })
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    // TODO directorPositionTermStartDateとdirectorPositionTermEndDateはNullはないはずなので、型を変えた時にNull対応処理を消す
    comparePositionData = (a: PositionData, b: PositionData): number => {
        const aStartDate = a.directorPositionTermStartDate ?? moment('1000-01-01')
        const bStartDate = b.directorPositionTermStartDate ?? moment('1000-01-01')
        if (!aStartDate.isSame(bStartDate, 'day')) {
            return aStartDate.isAfter(bStartDate, 'day') ? 1 : -1
        }
        const aEndDate = a.directorPositionTermEndDate ?? moment('1000-01-01')
        const bEndDate = b.directorPositionTermEndDate ?? moment('1000-01-01')
        if (!aEndDate.isSame(bEndDate, 'day')) {
            return aEndDate.isAfter(bEndDate, 'day') ? 1 : -1
        }
        const aName = `${a.roomList[0]?.ridgeCd}${a.roomList[0]?.roomNumber}`
        const bName = `${b.roomList[0]?.ridgeCd}${b.roomList[0]?.roomNumber}`
        if (aName === bName) {
            return 0
        }
        return (aName > bName) ? 1 : -1
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    handleClickSelectAssocDirectorForChairman = (): void => {
        this.beforeUserAction()
        this.setState({dialogOpenRole: ROLE_FOR_ADMINISTRATOR_REAL.ASSOC_CHAIRMAN})
    }

    handleClickSelectAssocDirectorForViceChairman = (): void => {
        this.beforeUserAction()
        this.setState({dialogOpenRole: ROLE_FOR_ADMINISTRATOR_REAL.ASSOC_VICE_CHAIRMAN})
    }

    handleClickDialogCancelButton = () => {
        this.setState({dialogOpenRole: false})
    }

    handleClickRegisterAssocDirectorButton = (assocDirectorId: number) => async (): Promise<void> => {
        const {eventEmitter} = this.props

        if (this.state.dialogOpenRole === false) {
            return
        }

        await eventEmitter.showLoading()
        try {
            const information = await this.props.asyncCallFunction(
                this.props.assocDirectorRepo.registerAssocDirectorPosition.bind(
                    this.props.assocDirectorRepo,
                    {
                        condominiumCd: this.props.condominiumCd,
                        assocId: this.state.selectedAssocId,
                        assocTermId: this.state.assocTerm.assocTermId,
                        assocDirectorId: assocDirectorId,
                        role: this.state.dialogOpenRole,
                    },
                ),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (information === null) {
                return
            }

            await eventEmitter.showHeadingAreaMessage({
                title: information.title || '',
                messageLevel: information.type,
                detail: information.message,
            })
        } finally {
            this.setState({dialogOpenRole: false})
            eventEmitter.closeLoading()
        }

        await this.updatePositionList()
    }

    handleClickEditPositionButton = (position: PositionData) => () => {
        this.beforeUserAction()
        this.setState({
            editPositionData: position,
            editDialogMidtermAppointmentDate: position.midtermAppointmentDate?.toDate() ?? null,
            editDialogMidtermTerminationDate: position.midtermTerminationDate?.toDate() ?? null,
            updateWarnings: [],
            acceptAllUpdateWarnings: true,
        })
    }

    handleClickDeletePositionButton = (position: PositionData) => () => {
        this.beforeUserAction()
        const message = <>
            <Box fontSize={'16px'}>
                役職を削除します。よろしいですか？
            </Box>
            <Box fontSize={'14px'} lineHeight={'24px'}>
                ※役職を交代する場合は、削除せずに途中退任日を設定してください。
            </Box>
        </>
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: MESSAGE_TYPE.INFORMATION,
                message: message,
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: () => this.props.eventEmitter.closeDialog(),
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleClickOkOnDeleteConfirmDialog(position),
                    },
                ],
            },
        )
    }

    handleClickOkOnDeleteConfirmDialog = (position: PositionData) => async () => {
        const {assocDirectorRepo, condominiumCd, asyncCallFunction} = this.props
        const {selectedAssocId, assocTerm} = this.state

        await this.props.eventEmitter.showLoading()
        try {
            const deleteMessage: Nullable<Information> = await asyncCallFunction(
                assocDirectorRepo.deleteAssocDirectorPosition.bind(
                    assocDirectorRepo,
                    {
                        condominiumCd: condominiumCd,
                        assocId: selectedAssocId,
                        assocTermId: assocTerm.assocTermId,
                        assocDirectorId: position.assocDirectorId,
                        assocDirectorPositionId: position.assocDirectorPositionId,
                    },
                ),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (deleteMessage === null) {
                return
            }

            await this.props.eventEmitter.showHeadingAreaMessage({
                title: deleteMessage.title || '',
                messageLevel: deleteMessage.type,
                detail: deleteMessage.message,
            })
        } finally {
            this.props.eventEmitter.closeDialog()
            this.props.eventEmitter.closeLoading()
        }

        await this.updatePositionList()
    }

    handleChangeEditDialogMidtermAppointmentDate = (date: Nullable<Date>) => {
        this.setState({editDialogMidtermAppointmentDate: date})
    }

    handleChangeEditDialogMidtermTerminationDate = (date: Nullable<Date>) => {
        this.setState({editDialogMidtermTerminationDate: date})
    }

    handleChangeEditDialogAcceptedWarning = (acceptAll: boolean) => {
        this.setState({acceptAllUpdateWarnings: acceptAll})
    }

    handleClickEditDialogCancelButton = () => {
        this.setState({editPositionData: null})
    }

    handleClickEditDialogRegisterButton = async () => {
        const {eventEmitter} = this.props
        const {
            editPositionData,
            editDialogMidtermAppointmentDate,
            editDialogMidtermTerminationDate,
            updateWarnings,
        } = this.state
        if (!editPositionData) {
            return
        }

        await eventEmitter.showLoading()
        let information: Information
        try {
            const informationList = await this.props.asyncCallFunction(
                this.props.assocDirectorRepo.updateAssocDirectorPosition.bind(
                    this.props.assocDirectorRepo,
                    {
                        condominiumCd: this.props.condominiumCd,
                        assocId: this.state.selectedAssocId,
                        assocTermId: this.state.assocTerm.assocTermId,
                        assocDirectorId: editPositionData.assocDirectorId,
                        assocDirectorPositionId: editPositionData.assocDirectorPositionId,
                        updateAssocDirectorPosition: {
                            midtermAppointmentDate: editDialogMidtermAppointmentDate,
                            midtermTerminationDate: editDialogMidtermTerminationDate,
                            version: editPositionData.version,
                        },
                        acceptedWarnings: updateWarnings.map(warning => ({messageId: warning.messageId})),
                    },
                ),
                (httpErrorOrNetworkError) => {
                    this.setState({editPositionData: null})
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )
            if (informationList === null || informationList.length === 0) {
                return
            }

            information = informationList[0]
            if (information.type === MESSAGE_TYPE.WARNING) {
                this.setState({
                    updateWarnings: informationList,
                    acceptAllUpdateWarnings: false,
                })
                return
            }
        } finally {
            eventEmitter.closeLoading()
        }

        await eventEmitter.showHeadingAreaMessage({
            title: information.title || '',
            messageLevel: information.type,
            detail: information.message,
        })
        this.setState({editPositionData: null})

        await this.updatePositionList()
    }

    renderPositionTable(positions: PositionData[], currentDateTime: Moment): ReactNode {
        return <Table>
            <LightGrayTableHead>
                <PositionTableRow>
                    <HeadCell>役職の在任期間</HeadCell>
                    <HeadCell>
                        <div>部屋番号</div>
                        <WidthBuffer>ああああああ</WidthBuffer>
                    </HeadCell>
                    <HeadCell>
                        <div>氏名</div>
                        <WidthBuffer>あああああ いいいいい</WidthBuffer>
                    </HeadCell>
                    <HeadCell></HeadCell>
                </PositionTableRow>
            </LightGrayTableHead>
            <TableBody>
                {positions.map((position, index1) => (
                    <PositionTableRow key={index1}>
                        <TermCell>
                            {makeTermNode(position.directorPositionTermStartDate, position.directorPositionTermEndDate)}
                        </TermCell>
                        <RoomCell>
                            {AssocDirectorHelper.getRoomNumberNodeByResidentStatus(position.roomList)}
                        </RoomCell>
                        <NameCell>
                            {AssocDirectorHelper.getDisplayUserNameNodeByResidentStatus(
                                position.familyName, position.firstName, position.roomList,
                            )}
                        </NameCell>
                        <OperationCell>
                            <EditLink onClick={this.handleClickEditPositionButton(position)}>
                                途中着任日・退任日の編集
                            </EditLink>
                            <Box display={'inline-block'} width={'16px'}/>
                            <HideableDeleteLink
                                onClick={this.handleClickDeletePositionButton(position)}
                                visibility={this.showDeleteLink(position, currentDateTime) ? 'visible' : 'hidden'}
                            >
                                削除
                            </HideableDeleteLink>
                        </OperationCell>
                    </PositionTableRow>
                ))}
            </TableBody>
        </Table>
    }

    showDeleteLink = (position: PositionData, currentDateTime: Moment): boolean => {
        return judgeRoleCheck([ROLE_FOR_ADMINISTRATOR.SYSTEM_ADMIN.value])
            || (position.midtermTerminationDate?.isSameOrAfter(currentDateTime, 'day') ?? true)
    }

    renderAssocDirectorSelectDialogTable(assocDirectorList: AssocDirector[]): ReactNode {
        return <>
            <Table stickyHeader>
                <TableHead>
                    <AssocDirectorSelectTableRow>
                        <HeadCell>部屋番号</HeadCell>
                        <HeadCell>氏名</HeadCell>
                        <HeadCell>
                            <NoWrapSpan>役員向け機能の</NoWrapSpan>
                            <NoWrapSpan>利用期間</NoWrapSpan>
                        </HeadCell>
                        <HeadCell></HeadCell>
                    </AssocDirectorSelectTableRow>
                </TableHead>
                <TableBody>
                    {assocDirectorList.map((assocDirector, index1) => (
                        <AssocDirectorSelectTableRow key={index1}>
                            <RoomCell>
                                {AssocDirectorHelper.getRoomNumberNodeByResidentStatus(assocDirector.roomList)}
                            </RoomCell>
                            <NameCell>
                                {AssocDirectorHelper.getDisplayUserNameNodeByResidentStatus(
                                    assocDirector.familyName,
                                    assocDirector.firstName,
                                    assocDirector.roomList,
                                )}
                            </NameCell>
                            <TermCell>
                                {makeTermNode(
                                    assocDirector.availableTermStartDate,
                                    assocDirector.availableTermEndDate,
                                )}
                            </TermCell>
                            <SelectButtonCell>
                                <AdminPrimaryButton onClick={
                                    this.handleClickRegisterAssocDirectorButton(assocDirector.assocDirectorId)
                                }>
                                    選択
                                </AdminPrimaryButton>
                            </SelectButtonCell>
                        </AssocDirectorSelectTableRow>
                    ))}
                </TableBody>
            </Table>
        </>
    }

    renderEditPositionDialog(position: PositionData): ReactNode {
        const {
            editDialogMidtermAppointmentDate,
            editDialogMidtermTerminationDate,
            updateWarnings,
            acceptAllUpdateWarnings,
            assocDirectorList,
        } = this.state
        const assocTermStartDate = this.state.assocTerm.assocTermStartDate.toDate()
        const assocTermEndDate = this.state.assocTerm.assocTermEndDate.toDate()
        const assocDirector = assocDirectorList.find(item => item.assocDirectorId === position.assocDirectorId)

        return <>
            <Dialog
                id={'pp-dialog'}
                open={true}
                transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                maxWidth={'lg'}
            >
                <DialogArea>
                    <StyledDialogTitle>
                        <AdminPageHeadingArea title={'途中着任日・退任日の編集'}/>
                    </StyledDialogTitle>
                    <StyledDialogContent>
                        <PrevAssocDirectorInfoArea>
                            <LeftRowFlexContainer>
                                <div>{AssocDirectorHelper.getJoinedRoomNumberNodeByResidentStatus(
                                    position.roomList,
                                )}</div>
                                <Box ml={'16px'}>
                                    {AssocDirectorHelper.getDisplayUserNameByResidentStatus(
                                        position.familyName, position.firstName, position.roomList,
                                    )}
                                </Box>
                            </LeftRowFlexContainer>
                            <EditPositionDialogTerm>
                                役員向け機能の利用期間：
                                {formatTerm(assocDirector!.availableTermStartDate, assocDirector!.availableTermEndDate)}
                            </EditPositionDialogTerm>
                        </PrevAssocDirectorInfoArea>
                        <Box height={'24px'}/>
                        <LeftRowFlexContainer>
                            <div>役職</div>
                            <EditPositionDialogPosition>
                                {ROLE_FOR_ADMINISTRATOR_REAL.codeToEnumConstants(position.positionRoleId).label}
                            </EditPositionDialogPosition>
                        </LeftRowFlexContainer>
                        <Box height={'16px'}/>
                        <LeftRowFlexContainer>
                            <MidtermFormLabel>
                                <div>途中着任日</div>
                                <PPDatePicker
                                    value={editDialogMidtermAppointmentDate}
                                    onChangeDate={this.handleChangeEditDialogMidtermAppointmentDate}
                                    minDate={assocTermStartDate}
                                    maxDate={editDialogMidtermTerminationDate ?? assocTermEndDate}
                                />
                            </MidtermFormLabel>
                            <Box width={'40px'}/>
                            <MidtermFormLabel>
                                <div>途中退任日</div>
                                <PPDatePicker
                                    value={editDialogMidtermTerminationDate}
                                    onChangeDate={this.handleChangeEditDialogMidtermTerminationDate}
                                    minDate={editDialogMidtermAppointmentDate ?? assocTermStartDate}
                                    maxDate={assocTermEndDate}
                                />
                            </MidtermFormLabel>
                        </LeftRowFlexContainer>
                        <Box height={'4px'}/>
                        <EditPositionDialogInfo>
                            ※理事会の任期の途中で役職に着任する場合は「途中着任日」を、退任する場合は「途中退任日」を入力してください。
                        </EditPositionDialogInfo>
                        <NatAdminCommonAcceptedWarningComponent
                            description={'問題なければチェックしたうえで登録するボタンを押してください。'}
                            warnings={updateWarnings}
                            changeAcceptedWarnings={this.handleChangeEditDialogAcceptedWarning}
                            doOpenAnimation={true}
                        />
                    </StyledDialogContent>
                    <StyledDialogActions>
                        <BackLink onClick={this.handleClickEditDialogCancelButton}>
                            キャンセル
                        </BackLink>
                        <AdminPrimaryButton
                            onClick={this.handleClickEditDialogRegisterButton}
                            disabled={!acceptAllUpdateWarnings}
                        >
                            登録する
                        </AdminPrimaryButton>
                    </StyledDialogActions>
                </DialogArea>
            </Dialog>
        </>
    }

    render(): ReactNode {
        const {
            displayAssocName,
            currentDateTime,
            assocDirectorList,
            chairmanList,
            viceChairmanList,
            dialogOpenRole,
            editPositionData,
        } = this.state
        const {assocTermName, assocTermStartDate, assocTermEndDate} = this.state.assocTerm
        const assocTermNameAndTerm = `${assocTermName}（${formatTerm(assocTermStartDate, assocTermEndDate)}）`

        if (!currentDateTime) {
            return null
        }

        return <>
            <Box height={'9px'}/>
            {displayAssocName !== null && <>
                <LeftRowFlexContainer>
                    <AssocItemName>管理組合</AssocItemName>
                    <AssocItemValue>{displayAssocName}</AssocItemValue>
                </LeftRowFlexContainer>
                <Box height={'8px'}/>
            </>}
            <div>
                <AssocItemName>理事会の任期</AssocItemName>
                <AssocItemValue>{assocTermNameAndTerm}</AssocItemValue>
            </div>
            <Box height={'16px'}/>
            <WhiteArea>
                <LeftTopRowFlexContainer id={'chairman'}>
                    <PositionName>理事長</PositionName>
                    <LeftColumnFlexContainer>
                        <AdminDefaultButton onClick={this.handleClickSelectAssocDirectorForChairman}>
                            役員を選択する
                        </AdminDefaultButton>
                        {chairmanList.length > 0 && <>
                            <Box height={'16px'}/>
                            {this.renderPositionTable(chairmanList, currentDateTime)}
                            <Box height={'8px'}/>
                        </>}
                    </LeftColumnFlexContainer>
                </LeftTopRowFlexContainer>
                <LeftTopRowFlexContainer id={'vice-chairman'}>
                    <PositionName>副理事長</PositionName>
                    <LeftColumnFlexContainer>
                        <AdminDefaultButton onClick={this.handleClickSelectAssocDirectorForViceChairman}>
                            役員を選択する
                        </AdminDefaultButton>
                        {viceChairmanList.length > 0 && <>
                            <Box height={'16px'}/>
                            {this.renderPositionTable(viceChairmanList, currentDateTime)}
                            <Box height={'8px'}/>
                        </>}
                    </LeftColumnFlexContainer>
                </LeftTopRowFlexContainer>
            </WhiteArea>
            <Box height={'16px'}/>
            <div>・理事会の任期の途中で役職に着任する場合は役員を選択後、「途中着任日」を設定してください。</div>
            <div>・理事会の任期の途中で役職を退任する場合は、「途中退任日」を設定してください。</div>
            <div>・役職の在任期間が終了すると、役職を削除できなくなります。削除したい場合は、システム管理者へご連絡ください。</div>
            <Box height={'16px'}/>
            <BackLink
                onClick={() => this.props.navigate(
                    `/admin/condominiums/${this.props.condominiumCd}/assoc-directors`,
                )}
            >
                役員一覧に戻る
            </BackLink>

            <Dialog
                id={'pp-dialog'}
                open={dialogOpenRole !== false}
                transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                maxWidth={'lg'}
            >
                <StyledDialogTitle>
                    <AdminPageHeadingArea title={'役員選択'}/>
                </StyledDialogTitle>
                <StyledDialogContent>
                    <Paper>
                        {this.renderAssocDirectorSelectDialogTable(assocDirectorList)}
                    </Paper>
                </StyledDialogContent>
                <StyledDialogActions>
                    <BackLink
                        onClick={this.handleClickDialogCancelButton}
                    >
                        キャンセル
                    </BackLink>
                </StyledDialogActions>
            </Dialog>

            {editPositionData &&
                this.renderEditPositionDialog(editPositionData)
            }
        </>
    }
}

const AssocItemName = styled.span`
  display: inline-block;
  min-width: 124px;
`

const AssocItemValue = styled.span`
  font-weight: ${FontWeights.Bold};
  font-size: ${FontSizeInPixel.Body}px;
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const WhiteArea = styled(Paper)`
  & > div {
    padding: 16px;
  }

  & > div:not(:last-child) {
    border-bottom: solid 1px ${Colors.AccentLight};
  }
`

const PositionName = styled.div`
  min-width: 136px;
  line-height: 42px;
`

const PositionTableRow = styled(TableRow)`
  & > .MuiTableCell-root:first-child {
    padding-left: 16px;
  }

  & > .MuiTableCell-root:last-child {
    padding-right: 24px;
  }
`

const AssocDirectorSelectTableRow = styled(TableRow)`
  & > .MuiTableCell-root:first-child {
    padding-left: 40px;
  }
`

const TableCellHeightPaddingCss = css`
  padding: 16px 0;
`

const HeadCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding: 8px 0;
  }
`

const TermCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellHeightPaddingCss};
    padding-right: 40px;
  }
`

const RoomCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellHeightPaddingCss};
    padding-right: 24px;
    width: auto;
  }
`

const NameCell = styled(TableCell)`
  &.MuiTableCell-root {
    ${TableCellHeightPaddingCss};
    padding-right: 40px;
  }
`

const OperationCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding: 14.5px 0;
    white-space: nowrap;
  }
`

const HideableDeleteLink = styled(DeleteLink)<{ visibility: 'visible' | 'hidden' }>`
  visibility: ${props => props.visibility}
`

const SelectButtonCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding: 8px 40px;
    white-space: nowrap;
  }
`

const DialogArea = styled.div`
  width: 696px;
`

const PrevAssocDirectorInfoArea = styled.div`
  background: ${Colors.Background};
  padding: 8px 0 8px 8px;
  font-weight: bold;
  font-size: 16px;
`

const EditPositionDialogTerm = styled.div`
  padding-top: 4px;
  font-weight: ${FontWeights.Normal};
  font-size: 12px;
`

const EditPositionDialogPosition = styled.div`
  padding-left: 16px;
  font-weight: ${FontWeights.Bold};
  font-size: 14px;
`

const EditPositionDialogInfo = styled.div`
  font-size: 12px;
`

const MidtermFormLabel = styled.label`
  display: inline-block;
  width: 200px;
`

const StyledDialogTitle = withStyles({
    root: {
        padding: '24px 24px 6px 24px',
    },
})(DialogTitle)

const StyledDialogContent = withStyles({
    root: {
        padding: '0 24px 12px 24px',
    },
})(DialogContent)

const StyledDialogActions = withStyles({
    root: {
        padding: '12px 24px 24px 24px',
        justifyContent: 'space-between',
    },
})(DialogActions)

export default createAdminPage(AdminAssocDirectorPositionListPage)
